import { config } from '../config';
import { authenticatedFetch } from './api';

export interface AgentSession {
  sessionId: string;
  userId: string;
  orgId?: string;
  email: string;
  role: string;
  conversationHistory: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
  }>;
  context: {
    currentTask?: string;
    activeWorkflow?: string;
    memoryReferences: string[];
    lastIntent?: string;
  };
  createdAt: string;
  lastActive: string;
  expiresAt: string;
  deviceInfo: {
    userAgent: string;
    platform: string;
  };
  syncVersion: number;
  activeDevices: Array<{
    deviceId: string;
    lastSeen: string;
  }>;
}

const LOCAL_KEY = 'agent_session';
const SYNC_INTERVAL_MS = 30000;

export class AgentSessionClient {
  private apiBase: string;
  private syncTimer: ReturnType<typeof setInterval> | null = null;
  private userId: string = '';

  constructor() {
    this.apiBase = config.API_BASE_URL;
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  private getLocalSession(): AgentSession | null {
    try {
      const stored = localStorage.getItem(`${LOCAL_KEY}_${this.userId}`);
      if (!stored) {return null;}
      return JSON.parse(stored) as AgentSession;
    } catch (err) {
      console.error('Local session load error:', err);
      return null;
    }
  }
  private setLocalSession(session: AgentSession | null) {
    if (session) {
      localStorage.setItem(`${LOCAL_KEY}_${this.userId}`, JSON.stringify(session));
    } else {
      localStorage.removeItem(`${LOCAL_KEY}_${this.userId}`);
    }
  }

  async saveSession(sessionData: Partial<AgentSession>): Promise<AgentSession | null> {
    try {
      const response = await authenticatedFetch(`${this.apiBase}/api/session/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: this.userId,
          email: this.userId,
          sessionData,
        }),
      });
      if (!response.ok) {throw new Error(`Save failed: ${response.statusText}`);}
      const { session } = await response.json();
      this.setLocalSession(session);
      return session;
    } catch (err) {
      console.warn('[AgentSession] Server save failed, using localStorage fallback:', err);
      const fallback: AgentSession = {
        sessionId: `local_${Date.now()}`,
        userId: this.userId,
        email: this.userId,
        role: 'CUSTOMER',
        conversationHistory: [],
        context: { memoryReferences: [] },
        createdAt: new Date().toISOString(),
        lastActive: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 86400 * 7 * 1000).toISOString(),
        deviceInfo: { userAgent: navigator.userAgent, platform: navigator.platform },
        syncVersion: 1,
        activeDevices: [],
        ...sessionData,
      };
      this.setLocalSession(fallback);
      return fallback;
    }
  }

  async restoreSession(): Promise<AgentSession | null> {
    try {
      const response = await authenticatedFetch(
        `${this.apiBase}/api/session/restore?userId=${encodeURIComponent(this.userId)}`
      );
      if (!response.ok) {throw new Error(`Restore failed: ${response.statusText}`);}
      const { session } = await response.json();
      if (session) {
        this.setLocalSession(session);
        return session;
      }
      return this.getLocalSession();
    } catch (err) {
      console.warn('[AgentSession] Server restore failed, using localStorage fallback:', err);
      return this.getLocalSession();
    }
  }

  async syncSession(sessionData: Partial<AgentSession>, baseVersion: number): Promise<AgentSession | null> {
    try {
      const response = await authenticatedFetch(`${this.apiBase}/api/session/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: this.userId,
          email: this.userId,
          sessionData,
          baseVersion,
        }),
      });
      if (response.status === 409) {
        const conflict = await response.json();
        console.warn('[AgentSession] Version conflict, reloading session:', conflict);
        return this.restoreSession();
      }
      if (!response.ok) {throw new Error(`Sync failed: ${response.statusText}`);}
      const { session } = await response.json();
      this.setLocalSession(session);
      return session;
    } catch (err) {
      console.warn('[AgentSession] Server sync failed, using localStorage fallback:', err);
      return this.saveSession(sessionData);
    }
  }

  clearSession(): void {
    this.setLocalSession(null);
    authenticatedFetch(`${this.apiBase}/api/session?userId=${encodeURIComponent(this.userId)}`, {
      method: 'DELETE',
    }).catch(() => {});
  }

  startAutoSync(getSession: () => Partial<AgentSession> | null) {
    this.stopAutoSync();
    this.syncTimer = setInterval(async () => {
      const data = getSession();
      if (data) {
        const local = this.getLocalSession();
        const baseVersion = local?.syncVersion ?? 0;
        await this.syncSession(data, baseVersion);
      }
    }, SYNC_INTERVAL_MS);
  }

  stopAutoSync() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }
}

export const agentSessionClient = new AgentSessionClient();
